<script>
  import moment from 'moment'
  import 'moment-timezone'
  import { DEFAULT_TIMEZONE, getDateFormat, getDateTimeFormat, US_COUNTRY } from '@base/configs'

  export default {
    methods: {
      /**
       * Format number based on market locale
       * US: 1,000.50
       * EU: 1.000,50 or 1 000,50
       * Format number based on market locale
       * @param {number} number - Number to format
       * @param {object} options - Formatting options
       * @param {number} options.precision - Decimal places (default: 2)
       * @param {boolean} options.acceptZero - Accept zero values (default: false)
       * @param {string} options.marketCountry - Market country code
       */
      formatNumber (number, options = {}) {
        const {
          precision = 2,
          acceptZero = false,
          marketCountry = null
        } = options

        if (number || (acceptZero && number !== null)) {
          // eslint-disable-next-line no-undef
          const locale = marketCountry === US_COUNTRY ? 'en-US' : LANGUAGE_CODE
          return Number(number).toLocaleString(
            locale,
            {
              minimumFractionDigits: precision,
              maximumFractionDigits: precision
            }
          )
        }
        return ''
      },

      /**
       * Converts locale string to number
       *
       *  • «100 000,50» → 100000.5   (fr, ru, da …)
       *  • «100,000.50» → 100000.5   (en, zh …)
       *  • «100.000,50» → 100000.5   (de, es …)
       *  • «100,00»     → 100        (without a thousand separators)
       */
      parseNumber (value) {
        if (value === null || value === undefined || value === '') return NaN
        if (typeof value === 'number') return value

        // 1) remove non-printable symbols
        let s = String(value).replace(/[\u00A0\u202F\s]/g, '').trim()

        // 2) detect the decimal part
        const lastComma = s.lastIndexOf(',')
        const lastDot   = s.lastIndexOf('.')
        const decimalSep = lastComma > lastDot ? ',' :
                           lastDot   > lastComma ? '.' : ''

        // 3) Remove a thousand separator
        if (decimalSep) {
          const thousandSep = decimalSep === ',' ? '\\.' : ','
          s = s.replace(new RegExp(thousandSep, 'g'), '')
        }

        // 4) Decimal to dot
        if (decimalSep && decimalSep !== '.') {
          s = s.replace(decimalSep, '.')
        }

        // 5) allow to use only eligible for parseFloat symbols
        s = s.replace(/[^0-9.+-Ee]/g, '')

        return parseFloat(s)
      },

      formatMoney (value, market, options = {}) {
        const currency = (market && market.currencyCode) || ''
        const formatted = this.formatNumber(value, {
          marketCountry: market && market.supportCountry && market.supportCountry.code,
          ...options,
        })
        return formatted ? `${formatted} ${currency}`.trim() : ''
      },

      /**
       * Format date based on market locale
       * US: MM/DD/YYYY
       * EU: DD-MM-YYYY or DD.MM.YYYY
       * @param {string|Date} rawDate - Date to format
       * @param {string} marketCountry - Market country code
       * @param {string} marketTimezone - Market timezone (optional, only used if rawDate has time component)
       */
      formatDate (rawDate, marketCountry = null, marketTimezone = null) {
        if (!rawDate) return ''

        const format = marketCountry === US_COUNTRY ? 'MM/DD/YYYY' : getDateFormat(marketCountry)

        const m = moment(rawDate)

        if (marketTimezone && m.format('HH:mm:ss') !== '00:00:00') {
          return m.tz(marketTimezone).format(format)
        }

        return m.format(format)
      },

      /**
       * Format datetime based on market locale with timezone conversion
       * US: MM/DD/YYYY hh:mm A (12-hour with AM/PM)
       * EU: DD-MM-YYYY HH:mm (24-hour)
       * @param {string|Date} rawDateTime - DateTime to format
       * @param {string} marketCountry - Market country code
       * @param {string} marketTimezone - Market timezone (default: Europe/Berlin)
       */
      formatDateTime (rawDateTime, marketCountry = null, marketTimezone = DEFAULT_TIMEZONE) {
        if (!rawDateTime) return ''

        const format = marketCountry === US_COUNTRY ? 'MM/DD/YYYY hh:mm A' : getDateTimeFormat(marketCountry)

        return moment(rawDateTime)
          .tz(marketTimezone || DEFAULT_TIMEZONE)
          .format(format)
      },

      /**
       * Format time based on market locale
       * US: hh:mm A (12-hour with AM/PM)
       * EU: HH:mm (24-hour)
       * @param {string} rawTime - Time to format (HH:mm format)
       * @param {string} marketCountry - Market country code
       */
      formatTime (rawTime, marketCountry = null) {
        if (!rawTime) return ''

        const usFormat = 'hh:mm A'
        const euFormat = 'HH:mm'
        const format = marketCountry === US_COUNTRY ? usFormat : euFormat

        return moment(rawTime, 'HH:mm').format(format)
      },
    }
  }
</script>
